import {
  Alert,
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography
} from '@customink/pigment-react';
import React from 'react';
import {Controller, useFormContext} from 'react-hook-form';

export default function StyleForm({colors, isLoading, styleId, success}) {
  const {
    control,
    formState: {errors, isSubmitting},
    setValue,
    trigger,
    watch
  } = useFormContext();
  const useLabImage = watch('useLabImage');
  const imengrave = watch('imengrave');
  const imdeboss = watch('imdeboss');

  const isLoadingOrSubmitting = isLoading || isSubmitting;

  return (
    <Box
      component="div"
      sx={{
        display: 'flex',
        flex: 1,
        flexDirection: 'column',
        height: '100%',
        justifyContent: 'space-between',
        minWidth: '20rem'
      }}>
      <Stack spacing={3}>
        <Typography variant="subtitle1" color="text.secondary">
          Style ID: <strong>{String(styleId)}</strong>
        </Typography>

        {success && (
          <Alert severity="success" sx={{mb: 2}}>
            Algolia attributes updated successfully
          </Alert>
        )}

        {/* Initial Image */}
        <Stack
          sx={{
            border: '1px solid #e0e0e0',
            borderRadius: 1,
            p: 2
          }}
          gap="1rem">
          <Typography variant="h6" gutterBottom>
            Initial Image
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
            Configure which type of image will be used as the initial product
            image on listing pages and search results.
          </Typography>
          <Stack spacing={2}>
            <Controller
              name="useLabImage"
              control={control}
              render={({field}) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={field.value}
                      onChange={(e) => {
                        field.onChange(e.target.checked);
                        // Clear colorId when disabling lab image since color selection only applies to lab images
                        if (!e.target.checked) {
                          setValue('colorId', null);
                        }
                      }}
                      disabled={isSubmitting}
                    />
                  }
                  label={
                    <Box>
                      <Typography component="span">Use lab image</Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{display: 'block'}}>
                        Enable to generate composite lab images with design
                        overlays. When disabled, uses standard product images
                        based on the selected color.
                      </Typography>
                    </Box>
                  }
                />
              )}
            />

            <Controller
              name="colorId"
              control={control}
              render={({field}) => (
                <FormControl
                  fullWidth
                  disabled={isLoadingOrSubmitting || !useLabImage}
                  error={!!errors.colorId}>
                  <InputLabel id="colorId-label">Initial Color</InputLabel>
                  <Select
                    id="colorId-select"
                    labelId="colorId-label"
                    value={field.value || ''}
                    label="Initial Color"
                    onChange={(e) => field.onChange(e.target.value || null)}>
                    <MenuItem value="">
                      <em>No selection</em>
                    </MenuItem>
                    {colors?.map((color) => (
                      <MenuItem key={color.id} value={String(color.id)}>
                        {color.name} ({color.id})
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.colorId && (
                    <Typography variant="caption" color="error" sx={{mt: 0.5}}>
                      {errors.colorId.message}
                    </Typography>
                  )}
                  <Typography variant="caption" color="text.secondary">
                    {useLabImage
                      ? 'Choose which product color variant will be shown as the initial image on listing pages and search results. Leave empty to use the first available color.'
                      : 'Color selection is only available when using lab images. Standard mode uses the default product color.'}
                  </Typography>
                </FormControl>
              )}
            />
          </Stack>
        </Stack>

        {/* Design Settings */}
        <Stack
          sx={{
            border: '1px solid #e0e0e0',
            borderRadius: 1,
            p: 2
          }}
          gap="1rem">
          <Typography variant="h6" gutterBottom>
            Design Settings
          </Typography>
          <Stack spacing={2}>
            <Controller
              name="design"
              control={control}
              render={({field}) => (
                <TextField
                  label="Design CID"
                  type="text"
                  value={field.value || ''}
                  onChange={field.onChange}
                  disabled={isLoadingOrSubmitting}
                  required
                  error={!!errors.design}
                  helperText={
                    errors.design?.message ||
                    'Required: Enter a valid Design CID for the lab image'
                  }
                  inputProps={{'data-testid': 'design-cid-input'}}
                  fullWidth
                />
              )}
            />

            <Controller
              name="autoNegate"
              control={control}
              render={({field}) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={field.value === 1}
                      onChange={(e) => field.onChange(e.target.checked ? 1 : 0)}
                      disabled={isLoadingOrSubmitting}
                    />
                  }
                  label="Auto Negate"
                />
              )}
            />
          </Stack>
        </Stack>

        {/* Image Processing */}
        <Stack
          sx={{
            border: '1px solid #e0e0e0',
            borderRadius: 1,
            p: 2
          }}
          gap="1rem">
          <Typography variant="h6" gutterBottom>
            Image Processing
          </Typography>
          <Stack spacing={2}>
            <Controller
              name="ixfm"
              control={control}
              render={({field}) => (
                <FormControl
                  fullWidth
                  disabled={isLoadingOrSubmitting}
                  error={!!errors.ixfm}>
                  <InputLabel id="ixfm-label">Image Format</InputLabel>
                  <Select
                    id="ixfm-select"
                    labelId="ixfm-label"
                    value={field.value || ''}
                    label="Image Format"
                    onChange={(e) => field.onChange(e.target.value)}>
                    <MenuItem value="">Default</MenuItem>
                    <MenuItem value="jpeg">JPEG</MenuItem>
                    <MenuItem value="jpg">JPG</MenuItem>
                    <MenuItem value="png">PNG</MenuItem>
                    <MenuItem value="webp">WebP</MenuItem>
                  </Select>
                  {errors.ixfm && (
                    <Typography variant="caption" color="error" sx={{mt: 0.5}}>
                      {errors.ixfm.message}
                    </Typography>
                  )}
                </FormControl>
              )}
            />

            <Controller
              name="ixq"
              control={control}
              render={({field}) => (
                <TextField
                  label="Image Quality"
                  type="number"
                  value={field.value || ''}
                  onChange={(e) =>
                    field.onChange(
                      e.target.value === '' ? null : Number(e.target.value)
                    )
                  }
                  disabled={isLoadingOrSubmitting}
                  error={!!errors.ixq}
                  helperText={errors.ixq?.message || 'Quality 1-100'}
                  inputProps={{max: 100, min: 1, step: 1}}
                  fullWidth
                />
              )}
            />

            {/* Image Width and Height fields are hidden but still part of form data */}

            <Controller
              name="ixbg"
              control={control}
              render={({field}) => (
                <Box>
                  <Stack
                    direction="row"
                    spacing={2}
                    alignItems="center"
                    justifyContent="flex-start">
                    <TextField
                      label="Background Color"
                      type="text"
                      value={field.value || ''}
                      onChange={field.onChange}
                      disabled={isLoadingOrSubmitting}
                      error={!!errors.ixbg}
                      helperText={
                        errors.ixbg?.message || 'Hex color code (e.g., #ffffff)'
                      }
                      sx={{flex: 1}}
                    />
                    <Box
                      component="input"
                      type="color"
                      value={field.value || '#ffffff'}
                      onChange={(e) => field.onChange(e.target.value)}
                      disabled={isLoadingOrSubmitting || !field.value}
                      sx={{
                        '&:disabled': {
                          cursor: 'not-allowed',
                          opacity: 0.6
                        },
                        border: '1px solid #ccc',
                        borderRadius: 1,
                        cursor: 'pointer',
                        height: 56,
                        width: 80
                      }}
                    />
                  </Stack>
                </Box>
              )}
            />
          </Stack>
        </Stack>

        {/* Placement Settings */}
        <Stack
          sx={{
            border: '1px solid #e0e0e0',
            borderRadius: 1,
            p: 2
          }}
          gap="1rem">
          <Typography variant="h6" gutterBottom>
            Placement Settings
          </Typography>
          <Stack spacing={2}>
            <Controller
              name="placeMax"
              control={control}
              render={({field}) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={field.value === 1}
                      onChange={(e) => field.onChange(e.target.checked ? 1 : 0)}
                      disabled={isLoadingOrSubmitting}
                    />
                  }
                  label="Place Max"
                />
              )}
            />

            <Controller
              name="placeMaxPct"
              control={control}
              render={({field}) => (
                <TextField
                  label="Place Max Percentage"
                  type="number"
                  value={field.value || ''}
                  onChange={field.onChange}
                  disabled={isLoadingOrSubmitting}
                  error={!!errors.placeMaxPct}
                  helperText={
                    errors.placeMaxPct?.message ||
                    'Decimal value (0-1, e.g., 0.8 for 80%)'
                  }
                  inputProps={{
                    max: 1,
                    min: 0,
                    step: 0.01
                  }}
                  fullWidth
                />
              )}
            />

            <Controller
              name="placeMaxCenter"
              control={control}
              render={({field}) => (
                <TextField
                  label="Place Max Center"
                  type="number"
                  value={field.value || ''}
                  onChange={field.onChange}
                  disabled={isLoadingOrSubmitting}
                  error={!!errors.placeMaxCenter}
                  helperText={
                    errors.placeMaxCenter?.message ||
                    'Decimal value (0-1, e.g., 0.8 for 80%)'
                  }
                  inputProps={{max: 1, min: 0, step: 0.01}}
                  fullWidth
                />
              )}
            />

            <Controller
              name="placeUseProduct"
              control={control}
              render={({field}) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={field.value === 1}
                      onChange={(e) => field.onChange(e.target.checked ? 1 : 0)}
                      disabled={isLoadingOrSubmitting}
                    />
                  }
                  label="Place Use Product"
                />
              )}
            />

            {/* placeUseView field is hidden but value is still submitted to API */}
            <Controller
              name="placeUseView"
              control={control}
              render={() => null}
            />
          </Stack>
        </Stack>

        {/* Advanced Rendering Effects - keeping the existing section */}
        <Stack
          sx={{
            border: '1px solid #e0e0e0',
            borderRadius: 1,
            p: 2
          }}
          gap="1rem">
          <Typography variant="h6" gutterBottom>
            Advanced Rendering Effects
          </Typography>

          {/* Show alert when both effects are selected and using lab image */}
          {useLabImage && imengrave && imdeboss && (
            <Alert severity="warning" sx={{mb: 2}}>
              <Typography variant="body2">
                <strong>Conflict Detected:</strong> Engraving and Debossing{' '}
                effects cannot both be enabled when using Lab Image mode. Please
                disable one of the effects to continue.
              </Typography>
            </Alert>
          )}

          <Stack spacing={2}>
            <Controller
              name="imengrave"
              control={control}
              render={({field}) => (
                <Box>
                  <FormControlLabel
                    disabled={isLoadingOrSubmitting}
                    control={
                      <Checkbox
                        checked={field.value}
                        onChange={(e) => {
                          field.onChange(e.target.checked);
                          if (!e.target.checked) {
                            setValue('opacity', null);
                          }
                          trigger('opacity');
                        }}
                        disabled={isLoadingOrSubmitting}
                      />
                    }
                    label="Engraving Effect"
                  />
                  {errors.imengrave && (
                    <Typography
                      variant="caption"
                      color="error"
                      sx={{display: 'block', mt: 0.5}}>
                      {errors.imengrave.message}
                    </Typography>
                  )}
                </Box>
              )}
            />

            <Controller
              name="opacity"
              control={control}
              render={({field}) => (
                <TextField
                  label="Opacity"
                  type="number"
                  value={field.value || ''}
                  onChange={(e) => {
                    field.onChange(e.target.value || null);
                  }}
                  disabled={isLoadingOrSubmitting || !imengrave}
                  error={!!errors.opacity}
                  helperText={
                    errors.opacity?.message ||
                    (imengrave
                      ? 'Required when engraving is enabled (0-1, e.g., 0.8 for 80%)'
                      : 'Transparency level (0-1, e.g., 0.8 for 80%)')
                  }
                  required={Boolean(imengrave)}
                  inputProps={{
                    inputMode: 'decimal',
                    max: 1,
                    min: 0,
                    step: 0.01
                  }}
                  fullWidth
                />
              )}
            />

            <Controller
              name="imdeboss"
              control={control}
              render={({field}) => (
                <Box>
                  <FormControlLabel
                    disabled={isLoadingOrSubmitting}
                    control={
                      <Checkbox
                        checked={field.value}
                        onChange={(e) => {
                          field.onChange(e.target.checked);
                          if (!e.target.checked) {
                            setValue('debopacity', null);
                          }
                          trigger('debopacity');
                        }}
                        disabled={isLoadingOrSubmitting}
                      />
                    }
                    label="Debossing Effect"
                  />
                  {errors.imdeboss && (
                    <Typography
                      variant="caption"
                      color="error"
                      sx={{display: 'block', mt: 0.5}}>
                      {errors.imdeboss.message}
                    </Typography>
                  )}
                </Box>
              )}
            />

            <Controller
              name="debopacity"
              control={control}
              render={({field}) => (
                <TextField
                  label="Deboss Opacity"
                  type="number"
                  value={field.value || ''}
                  onChange={(e) => {
                    field.onChange(e.target.value || null);
                    trigger('imdeboss');
                  }}
                  disabled={isLoadingOrSubmitting || !imdeboss}
                  error={!!errors.debopacity}
                  helperText={
                    errors.debopacity?.message ||
                    (imdeboss
                      ? 'Required when debossing is enabled (0-1, e.g., 0.8 for 80%)'
                      : 'Deboss opacity level (0-1, e.g., 0.8 for 80%)')
                  }
                  required={Boolean(imdeboss)}
                  inputProps={{
                    inputMode: 'decimal',
                    max: 1,
                    min: 0,
                    step: 0.01
                  }}
                  fullWidth
                />
              )}
            />

            <Controller
              name="noauto"
              control={control}
              render={({field}) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={field.value}
                      onChange={(e) => field.onChange(e.target.checked)}
                      disabled={isLoadingOrSubmitting}
                    />
                  }
                  label="Disable Auto Processing"
                />
              )}
            />
          </Stack>
        </Stack>
      </Stack>
    </Box>
  );
}
